import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API_BASE = `${BACKEND_URL}/api`;

export const api = axios.create({
  baseURL: API_BASE,
  withCredentials: true,
});

// V6.3 — Intercepteur : si le backend refuse pour "compte désactivé"
// (401 account_disabled) pendant une session active, rediriger l'utilisateur
// vers la page login avec le message adéquat.
api.interceptors.response.use(
  (r) => r,
  (err) => {
    try {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      const path = typeof window !== "undefined" ? window.location.pathname : "";
      if (status === 401 && detail === "account_disabled" && !path.startsWith("/login")) {
        window.location.href = "/login?error=account_disabled";
      }
    } catch (_) { /* ignore */ }
    return Promise.reject(err);
  }
);

export const STAGES = [
  { key: "prospect", label: "Prospect", short: "Prospect", pillLabel: "Prospect", pillColor: "slate" },
  { key: "compromis", label: "Compromis", short: "Compromis", pillLabel: "Compromis", pillColor: "gray" },
  { key: "envoi_banque", label: "En banque", short: "En banque", pillLabel: "En banque", pillColor: "blue" },
  { key: "accord_banque", label: "Accord banque", short: "Accord banque", pillLabel: "Accord banque", pillColor: "green" },
  { key: "accord_risque", label: "Accord risque", short: "Accord risque", pillLabel: "Accord risque", pillColor: "green" },
  { key: "offre_editee", label: "Offre éditée", short: "Offre éditée", pillLabel: "Offre éditée", pillColor: "green" },
  { key: "acte_authentique", label: "Acte authentique", short: "Acte", pillLabel: "Acte authentique", pillColor: "green" },
];

// Ordre = maturité commerciale strictement croissante.
// Lecture mentale gauche→droite, haut→bas (grid 3x2).
// V4.23 — Deux dimensions séparées (cf. PRD).
//   CONTACT_STATUS  : pilote les relances (cycle de communication avec le prospect)
//   PROJECT_MATURITY: pilote la priorité commerciale (avancement projet acheteur)
export const CONTACT_STATUS_OPTIONS = [
  { key: "a_appeler",   label: "À appeler" },
  { key: "rdv_a_faire", label: "RDV à faire" },
  { key: "rdv_fait",    label: "RDV fait" },
];

export const PROJECT_MATURITY_OPTIONS = [
  { key: "en_reflexion",   label: "En réflexion" },
  { key: "en_recherche",   label: "En recherche" },
  { key: "offre_en_cours", label: "Offre en cours" },
];

// LEGACY (gardé pour rétrocompatibilité — usage à supprimer progressivement)
export const CONTACT_TYPES = [
  { key: "a_appeler",     label: "À appeler" },
  { key: "rdv_a_faire",   label: "RDV à faire" },
  { key: "rdv_fait",      label: "RDV fait" },
  { key: "en_reflexion",  label: "En réflexion" },
  { key: "en_recherche",  label: "En recherche" },
  { key: "offre_en_cours",label: "Offre en cours" },
];

export const BANQUES_SUGGESTIONS = [
  "BNP", "Banque Populaire", "Caisse d'Épargne", "Crédit Agricole",
  "LCL", "Société Générale", "CIC", "Boursorama", "Crédit Mutuel", "HSBC",
];

export const PART_OPTIONS = [70, 75, 80, 85, 90];

export const formatEUR = (n) => {
  if (n === null || n === undefined || isNaN(n)) return "—";
  return new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(n);
};

export const formatEURCompact = (n) => {
  if (n === null || n === undefined || isNaN(n)) return "—";
  if (Math.abs(n) >= 1000) {
    const k = n / 1000;
    // Au-delà de 100K€ : entier (lisibilité). En-dessous : 1 décimale (vérification mentale).
    // JS omet naturellement les ".0" → 9.0 s'affiche "9", pas de "9.0K €" disgracieux.
    if (Math.abs(k) >= 100) {
      return `${Math.round(k)}K €`;
    }
    const oneDecimal = Math.round(k * 10) / 10;
    return `${oneDecimal}K €`;
  }
  return `${Math.round(n)} €`;
};

export const formatDate = (iso) => {
  if (!iso) return "—";
  try {
    const d = typeof iso === "string" ? new Date(iso) : iso;
    return new Intl.DateTimeFormat("fr-FR", { day: "2-digit", month: "short", year: "numeric" }).format(d);
  } catch { return "—"; }
};

export const stageIndex = (key) => STAGES.findIndex((s) => s.key === key);

export const getStatusPill = (dossier) => {
  if (!dossier) return null;
  if (dossier.status === "perdu") return { label: "Perdu", color: "red" };
  if (dossier.status === "signe") return { label: "Signé", color: "green" };
  // V3 — Pill "À envoyer" si mandat signé mais pas encore envoyé en banque
  if (dossier.mandat_signe && !dossier.date_envoi_banque && dossier.etape !== "envoi_banque") {
    return { label: "À envoyer", color: "orange" };
  }
  // V3 — Si dossier prospect avec mandat → utiliser libellé "À envoyer" (cohérence)
  if (dossier.etape === "prospect" && dossier.mandat_signe) {
    return { label: "À envoyer", color: "orange" };
  }
  const stage = STAGES.find((s) => s.key === dossier.etape);
  return stage ? { label: stage.pillLabel, color: stage.pillColor } : { label: "En cours", color: "blue" };
};

// days between today and an ISO date (YYYY-MM-DD). Negative = overdue.
export const daysUntil = (iso) => {
  if (!iso) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const d = new Date(iso);
  d.setHours(0, 0, 0, 0);
  return Math.round((d - today) / (1000 * 60 * 60 * 24));
};

export const relanceLabel = (iso) => {
  const n = daysUntil(iso);
  if (n === null) return null;
  if (n < 0) return { txt: `en retard (${Math.abs(n)}j)`, tone: "red" };
  if (n === 0) return { txt: "aujourd'hui", tone: "orange" };
  if (n === 1) return { txt: "demain", tone: "orange" };
  return { txt: `dans ${n} jours`, tone: n <= 3 ? "orange" : "blue" };
};
